// The HTTP client's collections (PG-2b): a lender's saved requests in folders, shared by its team, from the Hub
// (GET / POST /admin/v1/lenders/{slug}/http-requests, PUT / DELETE /admin/v1/http-requests/{id}, POST …/import).
// The Hub empties a typed key on Save (keys are kept only as {{VARIABLES}}); on the fixtures (npm test) the same rule
// runs here, in memory.
import { api, apiMode } from '@/lib/hubApi'
import type { HttpSpec, SavedHttpRequest } from '@/lib/httpClient'

const memory: SavedHttpRequest[] = []
const newId = () => `hr_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
const KEYISH = /authori[sz]ation|api[-_]?key|apikey|token|secret|password|passwd|signature|client[-_]?secret|x-key|access[-_]?key|private/i
const hasVar = (v: string) => /\{\{\s*\$?[A-Za-z_][A-Za-z0-9_.-]*\s*\}\}/.test(v)
const cleanFolder = (f: string) => f.split('/').map((x) => x.trim()).filter(Boolean).join('/').slice(0, 200)

/** The fixtures' copy of the Hub's rule (api2/src/modules/http-client/strip-secrets.ts stripSecrets). */
function strip(spec: HttpSpec): HttpSpec {
  const s: HttpSpec = JSON.parse(JSON.stringify(spec))
  const clear = (v: string) => (v && !hasVar(v) ? '' : v)
  s.auth.token = clear(s.auth.token)
  s.auth.password = clear(s.auth.password)
  if (s.auth.type === 'apikey') s.auth.value = clear(s.auth.value)
  const rows = <T extends { key: string; value: string; type?: string }>(list: T[]) => list.map((x) => (KEYISH.test(x.key) && x.type !== 'file' ? { ...x, value: clear(x.value) } : x))
  s.headers = rows(s.headers)
  s.params = rows(s.params)
  s.body.urlencoded = rows(s.body.urlencoded)
  s.body.formdata = rows(s.body.formdata)
  s.url = s.url.replace(/([?&])([^=&#]+)=([^&#]*)/g, (whole, sep: string, k: string, v: string) => (KEYISH.test(k) && v && !hasVar(v) ? `${sep}${k}=` : whole))
  return s
}

const sorted = (list: SavedHttpRequest[]) => [...list].sort((a, b) => a.folder.localeCompare(b.folder) || a.name.localeCompare(b.name))

export const httpCollections = {
  async list(slug: string): Promise<SavedHttpRequest[]> {
    if (apiMode()) return api<SavedHttpRequest[]>('GET', `/lenders/${slug}/http-requests`)
    return sorted(memory.filter((r) => r.lenderSlug === slug))
  },
  async create(slug: string, folder: string, name: string, request: HttpSpec, by: string): Promise<SavedHttpRequest> {
    if (apiMode()) return api<SavedHttpRequest>('POST', `/lenders/${slug}/http-requests`, { folder, name, request })
    const it: SavedHttpRequest = { id: newId(), lenderSlug: slug, folder: cleanFolder(folder), name: name.trim(), request: strip(request), savedBy: by, savedAt: new Date().toISOString() }
    memory.push(it)
    return it
  },
  async update(id: string, patch: { folder?: string; name?: string; request?: HttpSpec }, by: string): Promise<SavedHttpRequest> {
    if (apiMode()) return api<SavedHttpRequest>('PUT', `/http-requests/${id}`, patch)
    const at = memory.findIndex((r) => r.id === id)
    if (at < 0) throw new Error('That saved request does not exist.')
    const cur = memory[at]!
    const it = { ...cur, folder: patch.folder !== undefined ? cleanFolder(patch.folder) : cur.folder, name: patch.name?.trim() ?? cur.name, request: patch.request ? strip(patch.request) : cur.request, savedBy: by, savedAt: new Date().toISOString() }
    memory[at] = it
    return it
  },
  async remove(id: string): Promise<void> {
    if (apiMode()) return void (await api('DELETE', `/http-requests/${id}`))
    const at = memory.findIndex((r) => r.id === id)
    if (at >= 0) memory.splice(at, 1)
  },
  async import(slug: string, source: string, items: { folder: string; name: string; request: HttpSpec }[], by: string): Promise<{ imported: number; stripped: number }> {
    if (apiMode()) return api('POST', `/lenders/${slug}/http-requests/import`, { source, items })
    for (const it of items) await httpCollections.create(slug, it.folder, it.name, it.request, by)
    return { imported: items.length, stripped: 0 }
  },
  /** for the tests */
  reset: () => void memory.splice(0),
}
